import { useEffect, useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Header from '../components/Header'

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay },
})

interface StatsData {
  visitors_30d: number
  sessions_30d: number
  page_views: number
  cta_clicks: number
  popup_opens: number
  popup_submits: number
  gifts_redeemed: number
  hunt_claims_total: number
  active_hunters: number
  top_countries: { country: string; count: number }[]
  total_codes: number
  updated_at: string
}

interface ClaimResult {
  valid: boolean
  egg?: string
  already?: boolean
  claimed?: string[]
  total?: number
  reason?: string
}

const EGGS_META: { id: string; n: number | string; label: string; hint: string }[] = [
  { id: 'console',          n: 1,  label: 'Console',           hint: 'F12 abre o terminal.' },
  { id: 'html',             n: 2,  label: 'HTML cru',          hint: 'Ctrl+U mostra a página por trás.' },
  { id: 'cookie',           n: 3,  label: 'Cookie',            hint: 'F12, Application, Cookies.' },
  { id: 'localstorage',     n: 4,  label: 'localStorage',      hint: 'F12, Application, Local Storage.' },
  { id: 'headers',          n: 5,  label: 'Response Headers',  hint: 'F12, Network, primeira request, Headers.' },
  { id: 'robots',           n: 6,  label: 'robots.txt',        hint: '/robots.txt.' },
  { id: 'humans',           n: 7,  label: 'humans.txt',        hint: '/humans.txt.' },
  { id: 'security',         n: 8,  label: 'security.txt',      hint: '/.well-known/security.txt.' },
  { id: 'sitemap',          n: 9,  label: 'sitemap.xml',       hint: '/sitemap.xml.' },
  { id: 'llms',             n: 10, label: 'llms.txt',          hint: '/llms.txt.' },
  { id: '404',              n: 11, label: 'Página 404',        hint: 'Qualquer rota inexistente.' },
  { id: 'sessionstorage',   n: 12, label: 'sessionStorage',    hint: 'F12, Application, Session Storage.' },
  { id: 'indexeddb',        n: 13, label: 'IndexedDB',         hint: 'F12, Application, IndexedDB, matheusedson.' },
  { id: 'manifest',         n: 14, label: 'Web Manifest',      hint: '/manifest.json.' },
  { id: 'performance',      n: 15, label: 'Performance Marks', hint: 'F12, Performance, gravar, parar, aba Timings.' },
  { id: 'heap',             n: 16, label: 'Heap Snapshot',     hint: 'F12, Memory, Take heap snapshot, busca GrowthEngineer.' },
  { id: 'sources',          n: 17, label: 'Source Maps',       hint: 'F12, Sources, src/main.tsx.' },
  { id: 'starthere',        n: 18, label: 'START-HERE.txt',    hint: '/START-HERE.txt.' },
  { id: 'meta-generator',   n: 19, label: 'Meta Generator',    hint: 'Ctrl+U, busca meta name="generator".' },
  { id: 'game',             n: 20, label: 'O Jogo',            hint: 'No console, digita game() e dá Enter.' },
  { id: 'css',              n: 21, label: 'CSS Custom Props',  hint: 'F12, Elements, html, Computed, filtra --.' },
]

const STORAGE_KEY = 'matheusedson_hunt_v1'
const SESSION_KEY = '__sid'
const STATS_CACHE_KEY = 'matheusedson_stats_cache'
const STATS_CACHE_TTL = 5 * 60 * 1000 // 5 min

function loadCachedStats(): StatsData | null {
  try {
    const raw = localStorage.getItem(STATS_CACHE_KEY)
    if (!raw) return null
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > STATS_CACHE_TTL) return null
    return data as StatsData
  } catch { return null }
}

function saveStatsCache(data: StatsData) {
  try { localStorage.setItem(STATS_CACHE_KEY, JSON.stringify({ data, ts: Date.now() })) }
  catch { /* ignore */ }
}

const TIERS = [
  { at: 3,  label: 'Mentoria 30min',           sub: 'Conversa 1:1 sobre algo específico do seu projeto.' },
  { at: 7,  label: 'Sessão estratégica 1h',    sub: 'Diagnóstico completo de growth do seu negócio. Pessoal.' },
  { at: 14, label: 'Audit em vídeo (Loom 5min)', sub: 'Você manda o seu site/funil, eu gravo um Loom de 5min com a leitura técnica.' },
  { at: 21, label: 'Hall of Fame + comunidade', sub: 'Seu nome no rank público com o tempo que levou, e acesso ao grupo só de quem zerou.' },
]

const APPOINTMENT_LINK = 'https://calendar.app.google/REPLACE_ME'
const WHATSAPP_GROUP_LINK = 'https://chat.whatsapp.com/REPLACE_ME'

function getSessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY)
  if (!sid) {
    sid = 's_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
    sessionStorage.setItem(SESSION_KEY, sid)
  }
  return sid
}

function loadLocal(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed.claimed) ? parsed.claimed : []
  } catch { return [] }
}

function saveLocal(claimed: string[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ claimed, updated: Date.now() })) }
  catch { /* ignore */ }
}

/** Funil de números do site — estilo barras decrescentes */
function FunnelBars({ data, loading }: { data: StatsData | null; loading: boolean }) {
  const steps = data ? [
    { label: 'Visitantes únicos', value: data.visitors_30d,    color: '#60a5fa', desc: 'Pessoas distintas (IP único, anonimizado) que abriram o site' },
    { label: 'Sessões',           value: data.sessions_30d,    color: '#818cf8', desc: 'Cada nova aba/dispositivo gera uma sessão' },
    { label: 'Pageviews',         value: data.page_views,      color: '#a78bfa', desc: 'Cada navegação dentro do site (home, /stats, /curriculo etc)' },
    { label: 'Engajaram',         value: data.hunt_claims_total, color: '#c084fc', desc: 'Quem chegou a clicar/codar e descobrir pelo menos 1 segredo' },
    { label: 'Cliques CTA',       value: data.cta_clicks,      color: '#e879f9', desc: 'Clique em "Mandar mensagem" / contato' },
    { label: 'Conversões',        value: data.popup_submits + data.gifts_redeemed, color: '#4ade80', desc: 'Brindes resgatados + mensagens enviadas' },
  ] : []

  const max = steps[0]?.value || 1

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
      {(loading ? [0, 1, 2, 3, 4, 5] : steps).map((s, i) => {
        if (loading) {
          return <div key={i} style={{ height: 48, background: 'var(--bg2)', borderRadius: 7, opacity: 0.5 }} />
        }
        const step = s as { label: string; value: number; color: string; desc: string }
        const pct = max > 0 ? Math.max((step.value / max) * 100, 2) : 0
        const prevValue = i > 0 ? (steps[i - 1] as { value: number }).value : null
        const convPct = prevValue && prevValue > 0 ? ((step.value / prevValue) * 100).toFixed(1) : null
        return (
          <div key={step.label} style={{ position: 'relative' }}>
            <div style={{ position: 'relative', height: 52, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: 'easeOut' }}
                style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  background: `linear-gradient(90deg, ${step.color}30 0%, ${step.color}10 100%)`,
                  borderRight: `2px solid ${step.color}`,
                }}
              />
              <div style={{ position: 'absolute', inset: 0, padding: '0 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{step.label}</span>
                  <span style={{ fontSize: '0.66rem', color: 'var(--muted)' }}>{step.desc}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.55rem', flexShrink: 0 }}>
                  {convPct !== null && (
                    <span style={{ fontSize: '0.7rem', color: step.color, fontFamily: 'monospace' }}>{convPct}%</span>
                  )}
                  <span style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.03em', fontFamily: 'monospace' }}>
                    {step.value.toLocaleString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function Stats() {
  const cached = loadCachedStats()
  const [data, setData] = useState<StatsData | null>(cached)
  const [loading, setLoading] = useState(cached === null)
  const [claimed, setClaimed] = useState<string[]>(loadLocal())
  const [input, setInput] = useState('')
  const [status, setStatus] = useState<'idle' | 'checking' | 'success' | 'already' | 'wrong'>('idle')
  const [lastEgg, setLastEgg] = useState<string | null>(null)
  const [finished, setFinished] = useState<{ name: string; seconds: number; at: string } | null>(null)
  const [firstAt, setFirstAt] = useState<string | null>(null)
  const [nameInput, setNameInput] = useState('')
  const [finishStatus, setFinishStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [ranking, setRanking] = useState<{ name: string; country: string | null; seconds: number; at: string }[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // stats: stale-while-revalidate — mostra cache imediato, atualiza em background
    fetch('/api-stats')
      .then(r => r.json())
      .then((d: StatsData) => { setData(d); setLoading(false); saveStatsCache(d) })
      .catch(() => setLoading(false))

    // claim + ranking: não bloqueiam o render principal
    const sid = getSessionId()
    fetch('/api-claim?session_id=' + encodeURIComponent(sid))
      .then(r => r.json())
      .then((d: { claimed?: string[]; first_at?: string; finished?: { name: string; seconds: number; at: string } | null }) => {
        if (d.claimed) {
          const merged = Array.from(new Set([...loadLocal(), ...d.claimed]))
          setClaimed(merged)
          saveLocal(merged)
        }
        if (d.first_at) setFirstAt(d.first_at)
        if (d.finished) setFinished(d.finished)
      })
      .catch(() => {})
    fetch('/api-ranking')
      .then(r => r.json())
      .then((d: { ranking?: { name: string; country: string | null; seconds: number; at: string }[] }) => {
        if (d.ranking) setRanking(d.ranking)
      })
      .catch(() => {})
  }, [])

  const handleFinish = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nameInput.trim() || finishStatus === 'sending') return
    setFinishStatus('sending')
    try {
      const res = await fetch('/api-finish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nameInput.trim(), session_id: getSessionId() }),
      })
      const d = await res.json()
      if (d.ok) {
        setFinished({ name: d.name || nameInput.trim(), seconds: d.seconds || 0, at: new Date().toISOString() })
        setFinishStatus('done')
        fetch('/api-ranking').then(r => r.json()).then((dd) => { if (dd.ranking) setRanking(dd.ranking) })
      } else {
        setFinishStatus('error')
      }
    } catch {
      setFinishStatus('error')
    }
  }

  const fmtTime = (s: number) => {
    if (s < 60) return `${s}s`
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    return `${h}h ${m}m`
  }

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || status === 'checking') return
    setStatus('checking')
    try {
      const res = await fetch('/api-claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: input, session_id: getSessionId(), page: '/stats' }),
      })
      const d: ClaimResult = await res.json()
      if (d.valid && d.egg) {
        const newClaimed = d.claimed || [...claimed, d.egg]
        setClaimed(newClaimed)
        saveLocal(newClaimed)
        setLastEgg(d.egg)
        setStatus(d.already ? 'already' : 'success')
        setInput('')
        setTimeout(() => setStatus('idle'), 2200)
      } else {
        setStatus('wrong')
        setTimeout(() => setStatus('idle'), 1800)
      }
    } catch {
      setStatus('wrong')
      setTimeout(() => setStatus('idle'), 1800)
    }
  }

  const count = claimed.length
  const total = EGGS_META.length
  const pct = (count / total) * 100
  const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
  const lastEggLabel = lastEgg ? EGGS_META.find(e => e.id === lastEgg)?.label : null
  const tier3  = count >= 3
  const tier7  = count >= 7
  const tier14 = count >= 14
  const tier21 = count >= 21

  return (
    <>
      <Header />
      <main style={{ maxWidth: 820, margin: '0 auto', padding: '3rem 1.5rem 6rem' }}>

        {/* HERO */}
        <motion.div {...fade(0)} style={{ marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.75rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Stats</p>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              fontSize: '0.65rem', padding: '0.15rem 0.5rem', borderRadius: 99,
              background: 'rgba(22,163,74,0.12)', color: '#4ade80',
              border: '1px solid rgba(74,222,128,0.2)',
            }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', display: 'inline-block', animation: 'pulse-dot 2s infinite' }} />
              ao vivo
            </span>
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '0.75rem' }}>
            Métricas públicas
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.65, maxWidth: 560 }}>
            Se você mede, você gerencia. Tudo no funil abaixo são dados reais coletados pelo meu próprio pipeline: tracking enriquecido pelo Cloudflare (geo + IP hash), gravado no Supabase, lido por Worker.
          </p>
        </motion.div>

        {/* FUNIL DE NÚMEROS — PRINCIPAL */}
        <motion.section {...fade(0.05)} style={{ marginBottom: '3.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h2 style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Funil completo · últimos 30 dias
            </h2>
            {data && (
              <span style={{ fontSize: '0.7rem', color: 'var(--muted)', opacity: 0.7 }}>
                {data.top_countries.length} {data.top_countries.length === 1 ? 'país' : 'países'} · cache 5min
              </span>
            )}
          </div>
          <FunnelBars data={data} loading={loading} />
          {data && data.top_countries.length > 0 && (
            <div style={{ marginTop: '1.25rem', padding: '0.85rem 1.1rem', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 1rem' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>De onde:</span>
              {data.top_countries.map((c, i) => (
                <span key={c.country} style={{ fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                  <span style={{ fontWeight: 600 }}>{c.country}</span>
                  <span style={{ color: 'var(--muted)', fontFamily: 'monospace', fontSize: '0.72rem' }}>{fmt(c.count)}</span>
                  {i < data.top_countries.length - 1 && <span style={{ color: 'var(--border)' }}>·</span>}
                </span>
              ))}
            </div>
          )}
        </motion.section>

        {/* DIVISOR */}
        <motion.div {...fade(0.08)} style={{ marginBottom: '2.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Caçada do Console</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </motion.div>

        {/* HERO CAÇADA */}
        <motion.div {...fade(0.1)} style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 'clamp(1.4rem, 3.5vw, 2rem)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: '0.6rem' }}>
            21 segredos no site. <span style={{ color: 'var(--muted)' }}>Quantos você acha?</span>
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: '0.88rem', lineHeight: 1.6, maxWidth: 560 }}>
            Cada esconderijo contém uma palavra-chave única. Achou? Cola aqui. {data ? `${data.active_hunters} caçadores ativos.` : ''}
          </p>
        </motion.div>

        {/* PROGRESSO */}
        <motion.div {...fade(0.12)} style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Seu progresso</span>
            <span style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '-0.02em' }}>
              {count}<span style={{ color: 'var(--muted)', fontWeight: 400 }}>/{total}</span>
            </span>
          </div>
          <div style={{ position: 'relative', height: 8, background: 'var(--bg2)', borderRadius: 99, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7 }}
              style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                background: tier21 ? 'linear-gradient(90deg, #4ade80, #60a5fa)' : 'linear-gradient(90deg, #60a5fa, #a78bfa)',
              }}
            />
            {TIERS.map(t => (
              <div key={t.at} style={{
                position: 'absolute', top: -1, bottom: -1,
                left: `${(t.at / total) * 100}%`, width: 2,
                background: count >= t.at ? '#4ade80' : 'var(--border)',
                transform: 'translateX(-1px)',
              }} />
            ))}
          </div>
        </motion.div>

        {/* INPUT CÓDIGO */}
        <motion.div {...fade(0.14)} style={{ marginBottom: '3rem' }}>
          <form onSubmit={handleClaim}>
            <div style={{
              padding: '0.4rem',
              background: 'var(--bg2)',
              border: `1px solid ${status === 'success' ? '#4ade80' : status === 'wrong' ? '#f87171' : 'var(--border)'}`,
              borderRadius: 10, display: 'flex', gap: '0.4rem', transition: 'border-color 0.2s',
            }}>
              <input
                ref={inputRef} type="text" value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Achou um código? Cola aqui."
                autoComplete="off" spellCheck={false}
                disabled={status === 'checking'}
                style={{
                  flex: 1, padding: '0.7rem 0.9rem', background: 'transparent',
                  border: 'none', outline: 'none', color: 'var(--text)',
                  fontSize: '0.95rem', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}
              />
              <button type="submit" disabled={!input.trim() || status === 'checking'}
                style={{
                  padding: '0 1.4rem', background: input.trim() ? 'var(--accent)' : 'var(--border)',
                  color: 'var(--text)', fontSize: '0.85rem', fontWeight: 600,
                  border: 'none', borderRadius: 7,
                  cursor: input.trim() ? 'pointer' : 'default',
                  transition: 'background 0.15s', opacity: status === 'checking' ? 0.6 : 1,
                }}
              >{status === 'checking' ? '...' : 'Conferir'}</button>
            </div>
            <AnimatePresence>
              {status === 'success' && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ fontSize: '0.78rem', color: '#4ade80', marginTop: '0.5rem' }}>
                  ✓ Achou! Esse é o esconderijo: <b>{lastEggLabel}</b>
                </motion.p>
              )}
              {status === 'already' && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                  Você já tinha esse: <b>{lastEggLabel}</b>. Procura outro.
                </motion.p>
              )}
              {status === 'wrong' && (
                <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ fontSize: '0.78rem', color: '#f87171', marginTop: '0.5rem' }}>
                  Esse não é. Tenta outro.
                </motion.p>
              )}
            </AnimatePresence>
          </form>
        </motion.div>

        {/* RECOMPENSAS */}
        <motion.section {...fade(0.16)} style={{ marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1rem' }}>
            Recompensas
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {TIERS.map(tier => {
              const unlocked = count >= tier.at
              return (
                <div key={tier.at} style={{
                  padding: '1rem 1.25rem',
                  border: `1px solid ${unlocked ? 'rgba(74,222,128,0.35)' : 'var(--border)'}`,
                  background: unlocked ? 'rgba(74,222,128,0.06)' : 'var(--bg2)',
                  borderRadius: 10,
                  display: 'grid', gridTemplateColumns: '3.5rem 1fr', gap: '1rem', alignItems: 'center',
                  transition: 'all 0.3s',
                }}>
                  <div style={{
                    fontSize: '1.4rem', fontWeight: 800, textAlign: 'center',
                    color: unlocked ? '#4ade80' : 'var(--muted)',
                    fontFamily: 'monospace', letterSpacing: '-0.05em',
                  }}>{tier.at}</div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.92rem', fontWeight: 600 }}>{tier.label}</span>
                      {unlocked && <span style={{ fontSize: '0.65rem', color: '#4ade80' }}>✓ desbloqueado</span>}
                    </div>
                    <p style={{ fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.5 }}>{tier.sub}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </motion.section>

        {tier3 && (
          <motion.section {...fade(0)} style={{ marginBottom: '3rem' }}>
            <div style={{ padding: '1.5rem', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 10 }}>
              <h2 style={{ fontSize: '0.7rem', color: '#4ade80', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                Mentoria 30min desbloqueada ✓
              </h2>
              <p style={{ fontSize: '0.92rem', marginBottom: '0.8rem', lineHeight: 1.5 }}>Você achou 3. Vamos conversar 30 minutos.</p>
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
                Manda um email com o assunto "Mentoria 30min" e o contexto do que quer falar. Respondo em até 24h com horários.
              </p>
              <a href="mailto:linharesmatheus29@gmail.com?subject=Mentoria 30min&body=Oi Matheus, achei 3 da ca%C3%A7ada. Quero conversar sobre:"
                style={{ display: 'inline-block', padding: '0.6rem 1.2rem', background: '#4ade80', color: '#0a0a0a', borderRadius: 7, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 700 }}
              >Pedir mentoria</a>
            </div>
          </motion.section>
        )}

        {tier7 && (
          <motion.section {...fade(0)} style={{ marginBottom: '3rem' }}>
            <div style={{ padding: '1.5rem', background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 10 }}>
              <h2 style={{ fontSize: '0.7rem', color: '#a78bfa', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                Sessão estratégica 1h desbloqueada ✓
              </h2>
              <p style={{ fontSize: '0.92rem', marginBottom: '0.8rem', lineHeight: 1.5 }}>
                Você achou 7. Hora de tirar 1h do meu calendário pra um diagnóstico de growth do seu negócio.
              </p>
              <a href={APPOINTMENT_LINK} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', padding: '0.6rem 1.2rem', background: '#a78bfa', color: '#0a0a0a', borderRadius: 7, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 700 }}
              >Agendar sessão (1h)</a>
              <p style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.6rem' }}>Abre Google Calendar com meus horários.</p>
            </div>
          </motion.section>
        )}

        {tier14 && (
          <motion.section {...fade(0)} style={{ marginBottom: '3rem' }}>
            <div style={{ padding: '1.5rem', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10 }}>
              <h2 style={{ fontSize: '0.7rem', color: '#fbbf24', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
                Audit em vídeo desbloqueado ✓
              </h2>
              <p style={{ fontSize: '0.92rem', marginBottom: '0.8rem', lineHeight: 1.5 }}>
                Você achou 14. Manda o link do seu site (ou de um funil específico) que eu gravo um Loom de 5 minutos com a leitura técnica.
              </p>
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
                Olho stack, rastreamento, SEO básico, gaps óbvios e oportunidades. Sem floreio.
              </p>
              <a href="mailto:linharesmatheus29@gmail.com?subject=Audit em v%C3%ADdeo&body=Oi Matheus, achei 14 da ca%C3%A7ada. Quero o audit do meu site:%0D%0A%0D%0AURL:%0D%0AContexto (o que voc%C3%AA quer que eu olhe):"
                style={{ display: 'inline-block', padding: '0.6rem 1.2rem', background: '#fbbf24', color: '#0a0a0a', borderRadius: 7, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 700 }}
              >Pedir audit em vídeo</a>
            </div>
          </motion.section>
        )}

        {tier21 && (
          <motion.section initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} style={{ marginBottom: '3rem' }}>
            <div style={{
              padding: '2rem',
              background: 'linear-gradient(135deg, rgba(74,222,128,0.12), rgba(96,165,250,0.12))',
              border: '1px solid rgba(74,222,128,0.4)',
              borderRadius: 12, textAlign: 'center',
            }}>
              <p style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🏆</p>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>Você achou os 21</h2>
              {firstAt && !finished && (
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '1rem', fontFamily: 'monospace' }}>
                  Tempo aproximado: {fmtTime(Math.round((Date.now() - new Date(firstAt).getTime()) / 1000))}
                </p>
              )}

              {finished ? (
                <>
                  <p style={{ fontSize: '0.95rem', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                    <b>{finished.name}</b>, você está no Hall of Fame com <b style={{ fontFamily: 'monospace' }}>{fmtTime(finished.seconds)}</b>.
                  </p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '1.5rem', lineHeight: 1.6, maxWidth: 480, margin: '0 auto 1.5rem' }}>
                    Bem-vindo ao grupo. É um espaço privado de quem foi até o fim. Networking, troca técnica e às vezes oportunidades.
                  </p>
                  <a href={WHATSAPP_GROUP_LINK} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-block', padding: '0.85rem 1.8rem', background: '#4ade80', color: '#0a0a0a', borderRadius: 8, textDecoration: 'none', fontSize: '0.95rem', fontWeight: 700 }}
                  >Entrar no grupo WhatsApp</a>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.88rem', color: 'var(--muted)', marginBottom: '1.5rem', lineHeight: 1.6, maxWidth: 480, margin: '0 auto 1.5rem' }}>
                    Coloca um nome (ou apelido) pra entrar no Hall of Fame público, ver o tempo que você levou e ganhar acesso ao grupo privado de quem zerou.
                  </p>
                  <form onSubmit={handleFinish} style={{ display: 'flex', gap: '0.5rem', maxWidth: 380, margin: '0 auto', justifyContent: 'center' }}>
                    <input
                      type="text" value={nameInput}
                      onChange={e => setNameInput(e.target.value)}
                      placeholder="Seu nome ou apelido"
                      maxLength={40}
                      disabled={finishStatus === 'sending'}
                      style={{
                        flex: 1, padding: '0.7rem 0.95rem',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(74,222,128,0.4)',
                        borderRadius: 7, outline: 'none',
                        color: 'var(--text)', fontSize: '0.9rem',
                        fontFamily: 'inherit',
                      }}
                    />
                    <button type="submit" disabled={!nameInput.trim() || finishStatus === 'sending'}
                      style={{
                        padding: '0 1.3rem', background: '#4ade80', color: '#0a0a0a',
                        border: 'none', borderRadius: 7,
                        fontWeight: 700, fontSize: '0.85rem',
                        cursor: nameInput.trim() ? 'pointer' : 'default',
                        opacity: nameInput.trim() ? 1 : 0.5,
                      }}
                    >{finishStatus === 'sending' ? '...' : 'Entrar'}</button>
                  </form>
                  {finishStatus === 'error' && (
                    <p style={{ fontSize: '0.75rem', color: '#f87171', marginTop: '0.6rem' }}>Erro ao registrar. Tenta de novo.</p>
                  )}
                </>
              )}
            </div>
          </motion.section>
        )}

        {/* HALL OF FAME PÚBLICO */}
        {ranking.length > 0 && (
          <motion.section {...fade(0.18)} style={{ marginBottom: '3rem' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h2 style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Hall of Fame
              </h2>
              <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                {ranking.length} {ranking.length === 1 ? 'pessoa zerou' : 'pessoas zeraram'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'var(--border)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {ranking.map((r, i) => {
                const isMe = finished && r.name === finished.name && r.seconds === finished.seconds
                return (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '2.5rem 1fr auto',
                    gap: '0.75rem', alignItems: 'center',
                    padding: '0.7rem 1rem',
                    background: isMe ? 'rgba(74,222,128,0.08)' : 'var(--bg2)',
                    fontSize: '0.85rem',
                  }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.85rem',
                      color: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#a16207' : 'var(--muted)',
                      fontWeight: i < 3 ? 700 : 500,
                    }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${(i + 1).toString().padStart(2, '0')}`}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600 }}>{r.name}</span>
                      {r.country && <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>{r.country}</span>}
                      {isMe && <span style={{ fontSize: '0.65rem', color: '#4ade80', background: 'rgba(74,222,128,0.15)', padding: '0.1rem 0.4rem', borderRadius: 3 }}>você</span>}
                    </span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--muted)' }}>{fmtTime(r.seconds)}</span>
                  </div>
                )
              })}
            </div>
          </motion.section>
        )}

        {/* GRID DE ESCONDERIJOS */}
        <motion.section {...fade(0.18)} style={{ marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1rem' }}>
            Esconderijos {count > 0 ? `(${count} achados)` : ''}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.5rem' }}>
            {EGGS_META.map(egg => {
              const found = claimed.includes(egg.id)
              return (
                <div key={egg.id} title={found ? egg.hint : 'Ainda secreto. Vai caçar.'} style={{
                  padding: '0.7rem 0.8rem',
                  background: found ? 'rgba(74,222,128,0.08)' : 'var(--bg2)',
                  border: `1px solid ${found ? 'rgba(74,222,128,0.35)' : 'var(--border)'}`,
                  borderRadius: 7, fontSize: '0.72rem',
                  display: 'flex', flexDirection: 'column', gap: '0.2rem',
                  cursor: 'help', transition: 'all 0.2s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.6rem', color: 'var(--muted)', fontFamily: 'monospace' }}>
                      {typeof egg.n === 'number' ? `${egg.n.toString().padStart(2, '0')}/21` : egg.n}
                    </span>
                    <span style={{ color: found ? '#4ade80' : 'var(--muted)', fontSize: '0.7rem' }}>{found ? '✓' : '???'}</span>
                  </div>
                  <span style={{ fontWeight: 500, color: found ? 'var(--text)' : 'var(--muted)' }}>
                    {found ? egg.label : '???'}
                  </span>
                </div>
              )
            })}
          </div>
        </motion.section>

        {/* SOBRE PIPELINE */}
        <motion.section {...fade(0.2)}>
          <h2 style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
            Sobre o pipeline
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: '0.8rem', lineHeight: 1.7 }}>
            Eventos do front vão via <code style={{ background: 'var(--bg2)', padding: '0.1rem 0.3rem', borderRadius: 3, fontSize: '0.74rem' }}>sendBeacon</code> pra um Cloudflare Worker, que enriquece com geo (CF-IPCountry) e grava no Supabase. O Worker também valida os códigos da caçada contra um dicionário interno, gravando claims com session_id e hash de IP. Tudo open source no GitHub.
          </p>
        </motion.section>

      </main>

      <style>{`
        @keyframes pulse-dot { 0%,100% { opacity:1 } 50% { opacity:0.4 } }
      `}</style>
    </>
  )
}
